import React, { useRef } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Section, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { I18n } from '@fiverr-private/i18n-react';
import { Video } from '@fiverr-private/media';
import { DEFAULT_FIVERR_LOCALE } from '@fiverr-private/localization';
import SafeComponent from '../../../logged_out_homepage/shared/SafeComponent';
import { videoEventsHandler } from '../../../logged_out_homepage/utils/videoEventsHandler';
import { useIsMobileLayout } from '../../../../shared/hooks/useIsMobileLayout';
import { useVideoSourceReload } from '../../../../shared/hooks/useVideoSourceReload';
import { HOW_FIVERR_WORKS_VIDEO_SOURCES } from './constants';

const eventParams = { component_name: 'HowFiverrWorksVideo' };

const HowFiverrWorksVideo = () => {
    const { locale } = getContext();
    const isMobileLayout = useIsMobileLayout(['default']);
    const videoRef = useRef<HTMLVideoElement>(null);
    const playCountRef = useRef(0);

    const videoSource = HOW_FIVERR_WORKS_VIDEO_SOURCES[locale] || HOW_FIVERR_WORKS_VIDEO_SOURCES[DEFAULT_FIVERR_LOCALE];
    const videoToRender = isMobileLayout ? videoSource?.mobile : videoSource?.desktop;

    useVideoSourceReload(videoRef, videoToRender?.src);

    const pauseEventsHandler = () => {
        if (!videoRef.current) {
            return;
        }
        videoEventsHandler({ actionType: 'Pause', params: eventParams });
    };

    const playEventsHandler = () => {
        if (!videoRef.current) {
            return;
        }

        if (videoRef.current?.autoplay) {
            if (playCountRef.current >= 1) {
                return videoEventsHandler({ actionType: 'Play', params: eventParams });
            }

            return (playCountRef.current += 1);
        }

        return videoEventsHandler({ actionType: 'Play', params: eventParams });
    };

    return (
        <Section paddingTop="0" paddingBottom="0">
            <Stack position="relative" height="auto" marginBottom="10" display="flex" direction="column" gap="0">
                <Typography
                    style={{ fontWeight: 400 }}
                    marginBottom={{ default: '6', md: '10' }}
                    letterSpacing="-0.03em"
                    as="h2"
                >
                    <I18n k="logged_out_homepage.de.how_fiverr_works.title" />
                </Typography>
                <Stack width="100%" height="100%" display="flex" position="relative">
                    <Video
                        width="100%"
                        height="100%"
                        autoPlay
                        muted
                        onPause={pauseEventsHandler}
                        onPlay={playEventsHandler}
                        playsInline
                        preload="auto"
                        loop={true}
                        ref={videoRef}
                        sources={videoToRender ? [videoToRender] : []}
                        aria-hidden
                        borderRadius="2xl"
                    />
                </Stack>
            </Stack>
        </Section>
    );
};

export default SafeComponent(HowFiverrWorksVideo);
